<script setup lang="ts">
import { ref } from 'vue'
import LaHausIcon from '@lahaus-roomie/roomie/src/assets/icons/v2/la-haus.svg'
import RoomieIcon from '@lahaus-roomie/roomie/src/components/RoomieIcon/index.vue'
import ShareIosIcon from '@lahaus-roomie/roomie/src/assets/icons/share-ios.svg'
import RoomieButtonLink from '@lahaus-roomie/roomie/src/components/RoomieButtonLink/index.vue'
import { useScreenCapture } from '@/composables/useScreenCapture'
import type { TechnicalSpecsComponentModule } from '@/components/BrandedV2/TechnicalSpecs/types'
import { slugify } from '@/utils/strings'

const props = defineProps<TechnicalSpecsComponentModule.Props>()
const { n, t } = useI18n()
const { $useLhId } = useNuxtApp()

const myElement = ref<HTMLElement | null>(null)
const fileName = `${slugify(props.projectName + '-' + Date.now())}.png`
const { setElement, captureAndDownload, isLoading } = useScreenCapture(fileName)

const captureScreenShot = async () => {
  if (myElement.value) {
    setElement(myElement.value)
    await captureAndDownload()
  }
}

</script>
<script lang="ts">
export default {
  name: 'TechnicalSpecs'
}
</script>

<template>
  <div class="technical-specs w-[320px]">
    <div class="flex justify-center items-center py-lh-24">
      <RoomieButtonLink
        id="share-technical-specs-button"
        variant="ghost"
        :class="['w-full', { 'cursor-wait': isLoading }]"
        :data-lh-id="$useLhId({...dataAttributes.dataLh, A: 'descargar', C: t('share')})"
        :disabled="isLoading"
        @click="captureScreenShot">
        <div class="w-full flex flex-row justify-end items-center gap-4">
          <RoomieIcon custom-styles="fill-pine-600 w-24 h-24 text-pine-600">
            <ShareIosIcon class="text-pine-600" />
          </RoomieIcon>

          <span class="text-pine-600">
            {{ isLoading ? t('downloading') : t('share') }}
          </span>
        </div>
      </RoomieButtonLink>
    </div>

    <div
      ref="myElement"
      class="w-full">
      <div class="w-full h-[256px]">
        <div class="flex items-center w-full h-[59px] bg-pine-600 px-lh-24">
          <RoomieIcon custom-styles="block w-[78px] h-[16px] text-lime-600">
            <LaHausIcon />
          </RoomieIcon>
        </div>

        <div class="bg-white py-lh-24 px-lh-12 h-full">
          <div class="text-pine-600 mb-lh-16">
            <p class="body-1-m">
              {{ neighborhoodName }}, {{ cityName }}
            </p>

            <h4 class="display-7">
              {{ projectName }}
            </h4>
          </div>

          <div class="grid grid-cols-2 gap-y-16">
            <div class="border-r border-gray2-600">
              <p class="body-3-m text-pine-500">
                {{ t('price_range') }}
              </p>

              <p class="body-2-sb text-pine-600">
                {{ n(projectPrice, 'currency') }} {{ currency }}
              </p>
            </div>

            <div class="pl-lh-16">
              <p class="body-3-m text-pine-500">
                {{ t('delivery_time') }}
              </p>

              <p class="body-3-sb text-pine-600">
                {{ deliveryTime }}
              </p>
            </div>

            <div class="border-r border-gray2-600">
              <p class="body-3-m text-pine-500">
                {{ t('area_range') }}
              </p>

              <p class="body-2-sb text-pine-600">
                {{ areaRange }}
              </p>
            </div>

            <div class="flex items-center pl-lh-16">
              <p class="body-2-sb text-pine-600">
                {{ companyName }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div class="flex w-full">
        <div class="w-full grid grid-cols-2 gap-2">
          <img
            v-for="(image, index) in images"
            :key="image"
            :src="`/api/buyer-front/proxy-image?url=${encodeURIComponent(`${image}?auto=format,compress,enhance&width=800`)}`"
            :alt="`project ${index}`"
            class="w-full object-cover aspect-square"
            :class="[
              index === 0 ? 'max-h-[180px]' : 'max-h-[170px]',
              { 'col-span-2': index === 0 && images.length > 0 }
            ]">
        </div>
      </div>
    </div>
  </div>
</template>

<i18n src="./i18n.json" lang="json"></i18n>

<style lang="scss">
.technical-specs {
  .roomie-button {
    &__content {
      @apply w-full;
    }
  }

}
</style>
